<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Search Section -->
      <b-row class="mb-4 mt-2">
        <b-col
          sm="12"
          md="10"
          lg="8"
          xl="6"
        >
          <validation-observer ref="ibEnrolmentRules">
            <b-form>
              <b-row class="align-items-end">
                <b-col md="9">
                  <InputField
                    id="name"
                    v-model="searchParams.accountNumber"
                    :label="$t('Account Number')"
                    :placeholder="$t('Enter account number')"
                    type="number"
                  />
                </b-col>
                <b-col
                  md="3"
                  class="d-flex align-items-end pb-1"
                >
                  <Button
                    :label="$t('Search')"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="handleSearch"
                  />
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-col>
      </b-row>

      <!-- Form Details Section -->
      <b-row v-if="showCustomerDetails">
        <b-col md="6">
          <InputField
            id="customer-id"
            v-model="ibFormData.customerID"
            :label="$t('Customer ID')"
            :placeholder="$t('Enter customer ID')"
            type="text"
            readonly
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="ibFormData.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter account name')"
            type="text"
            readonly
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="ibFormData.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
            readonly
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="email-address"
            v-model="ibFormData.email"
            :label="$t('Email Address')"
            :placeholder="$t('Enter Email Address')"
            type="text"
            readonly
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="branch-name"
            v-model="ibFormData.branchName"
            :label="$t('Branch Name')"
            :placeholder="$t('Enter branch name')"
            type="text"
            readonly
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="account-type"
            v-model="ibFormData.accountType"
            :label="$t('Account Type')"
            :placeholder="$t('Enter account type')"
            type="text"
            readonly
          />
        </b-col>
      </b-row>

      <!-- Centered Action Button -->
      <b-row
        v-if="showCustomerDetails"
        class="mt-4"
      >
        <b-col class="d-flex justify-content-center">
          <Button
            :label="$t('Create Internet Banking Profile')"
            size="md"
            :loading="isLoading"
            @click="handleCreate"
          />
        </b-col>
      </b-row>
    </div>
    <b-modal
      id="search-modal"
      ref="search-modal-lg"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Customer Details')"
      cancel-title-only
      @hidden="resetModal"
    >

      <b-table
        ref="customerListTable"
        select-mode="single"
        :items="customerList"
        responsive
        :fields="translatedTableHeaders"
        primary-key="id"
        selectable
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
        @row-selected="onSelectCustomer"
      >

        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>

      </b-table>

      <TablePagination
        v-model="currentPage"
        :from="from"
        :to="to"
        :total-rows="totalEntries"
        :per-page="perPage"
      />

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>

    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { toAmount } from '@/helpers/formatAmount'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    ValidationObserver,
    Button,
    InputField,
    BTable,
    TablePagination,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toAmount,
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      showCustomerDetails: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
      ],
      searchParams: {
        accountNumber: '',
      },
      ibFormData: {
        accountNumber: '',
        accountType: '',
        branchName: '',
        customerID: '',
        email: '',
        accountName: '',
      },
      customerList: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      searchCustomerAccount: 'account/searchCustomerAccountAction',
      createInternetBankingEnrolUser: 'eChannels/createInternetBankingEnrolUser',
    }),
    async handleSearch() {
      try {
        const isSearchParamsNotEmpty = Object.values(this.searchParams).every(params => (params === null || params === ''))
        if (!isSearchParamsNotEmpty) {
          this.showFormErrorAlert = false
          this.showSpinner = true
          this.isDisabled = true
          const response = await this.searchCustomerAccount({
            search: this.searchParams,
            page: this.currentPage,
            limit: this.perPage,
            permission: 'ONBOARD_IBANKING_USER',
          })
          const { status, data } = response
          if (status === 200 && data?.data) {
            this.customerList = data.data
            this.totalCount = data.recordCount
            this.showSpinner = false
            this.isDisabled = false
            this.$root.$emit('bv::show::modal', 'search-modal')
          } else {
            this.customerList = []
            this.totalCount = data.recordCount
            this.$root.$emit('bv::show::modal', 'search-modal')
          }
        } else {
          this.showFormErrorAlert = true
          this.showSpinner = false
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
        this.isDisabled = false
      }
    },
    onSelectCustomer(items) {
      const [customer] = items
      this.ibFormData = {
        ...customer,
      }
      this.hideModal('search-modal')
      this.showCustomerDetails = true
    },
    async handleCreate() {
      this.$refs.ibEnrolmentRules.validate().then(success => {
        if (success) {
          this.confirm(this.createInternetBankingEnrolment, { text: this.$t('Are you sure you want to setup mobile profile for this customer?') })
        }
      })
    },
    async createInternetBankingEnrolment() {
      try {
        this.isDisabled = true
        this.isLoading = true

        const customerType = this.ibFormData.customerType || this.ibFormData.CustomerType
        const accountType = this.ibFormData.accountType || this.ibFormData.AccountType
        const blockedAccountTypes = ['VirtualProxy', 'VirtualWallet', 'EnhancedVirtualProxy']

        if (customerType !== 'Individual' || blockedAccountTypes.includes(accountType)) {
          const msg = this.$t('Only individual customers with non-virtual accounts can be enrolled for Internet Banking.')
          this.alertError({ text: msg })
          this.isDisabled = false
          this.isLoading = false
          return
        }

        const formData = {
          accountNumber: this.ibFormData.accountNumber,
        }
        const response = await this.createInternetBankingEnrolUser({ formData, permission: 'ONBOARD_IBANKING_USER' })
        const { status, data } = response
        if (status === 200 && data) {
          this.alertSuccess({ text: data.message })
          this.searchParams.accountNumber = ''
          this.showCustomerDetails = false
        }
      } catch (error) {
        const failedText = this.$t('Your request to setup mobile profile failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${failedText} ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
        this.showCustomerDetails = false
      }
    },
  },
}
</script>
